import React, { useEffect } from 'react';
import { useRAGDocuments } from '../lib/rag-documents.context';

import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';

export const RAGDocuments = () => {
  const {
    documents,
    filteredDocuments,
    selectedDocument,
    queryText,
    queryResponse,
    searchTerm,
    isLoadingDocuments,
    isLoadingQuery,
    error,
    fetchDocuments,
    selectDocument,
    setQueryText,
    setSearchTerm,
    submitQuery,
    clearQuery,
    clearError
  } = useRAGDocuments();

  const { toast } = useToast();

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        variant: 'destructive',
      });
      clearError();
    }
  }, [error]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileName = (document: any) => {
    // First check for display_name in metadata (user-specified name)
    if (document.metadata?.display_name) {
      return document.metadata.display_name;
    }
    // Then check for filename in metadata (original filename)
    if (document.metadata?.filename) {
      return document.metadata.filename;
    }
    // Finally, extract filename from file_path as a last resort
    const pathParts = document.file_path.split('/');
    return pathParts[pathParts.length - 1];
  };

  return (
    <div className="min-h-screen bg-black p-6">
      
      <div className="max-w-7xl mx-auto">
        <div className="bg-[#121212] rounded-lg shadow-lg p-6 text-[#CECFD2]">
          <h1 className="text-xl font-bold mb-6 text-[#CECFD2]">Document Q&A</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Documents List */}
            <div className="lg:col-span-1">
              <Card className="bg-[#121212] border-gray-700 rounded-lg shadow-lg">
                <CardHeader>
                  <CardTitle className="text-[#CECFD2] font-bold">Available Documents</CardTitle>
                  <CardDescription className="text-[#CECFD2]">
                    Select a document to query
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Input
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-[#26222E] text-[#CECFD2] border-gray-700"
                    />
                  </div>
                  {isLoadingDocuments ? (
                    <div className="flex justify-center p-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#4E46FF] border-t-transparent"></div>
                    </div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="text-center p-4 text-[#CECFD2]">
                      {documents.length === 0 ? "No documents available" : "No matching documents found"}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredDocuments.map((doc) => (
                        <div
                          key={doc.id}
                          className={cn(
                            "p-3 rounded-lg cursor-pointer transition-colors border border-gray-500",
                            selectedDocument?.id === doc.id
                              ? 'bg-[#26222E] border-[#4E46FF]'
                              : 'hover:bg-[#26222E]'
                          )}
                          onClick={() => selectDocument(doc)}
                        >
                          <h3 className="font-medium truncate text-[#CECFD2]">{getFileName(doc)}</h3>
                          <p className="text-sm text-[#CECFD2]">
                            {formatDate(doc.created_at)}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {doc.metadata?.chunk_count && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#CECFD2]">
                                {doc.metadata.chunk_count} chunks
                              </span>
                            )}
                            {doc.metadata?.file_type && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#CECFD2]">
                                {doc.metadata.file_type}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={fetchDocuments} 
                    disabled={isLoadingDocuments}
                    className={cn(
                      "w-full",
                      isLoadingDocuments 
                        ? "bg-[#26222E] opacity-50" 
                        : "bg-[#4E46FF] hover:bg-[#3d37cc]"
                    )}
                  >
                    {isLoadingDocuments ? 'Refreshing...' : 'Refresh Documents'}
                  </Button>
                </CardFooter>
              </Card>
            </div>
            
            {/* Query Section */}
            <div className="lg:col-span-2">
              <Card className="bg-[#121212] border-gray-700 rounded-lg shadow-lg">
                <CardHeader>
                  <CardTitle className="text-[#CECFD2] font-bold">
                    {selectedDocument 
                      ? `Query: ${getFileName(selectedDocument)}` 
                      : 'Select a document to query'}
                  </CardTitle>
                  <CardDescription className="text-[#CECFD2]">
                    Ask questions about the selected document
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedDocument ? (
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          value={queryText}
                          onChange={(e) => setQueryText(e.target.value)}
                          placeholder="Enter your question..."
                          className="flex-1 bg-[#26222E] text-[#CECFD2] border-gray-700"
                          disabled={isLoadingQuery}
                        />
                        <Button 
                          onClick={submitQuery} 
                          disabled={!queryText.trim() || isLoadingQuery}
                          className={cn(
                            !queryText.trim() || isLoadingQuery 
                              ? "bg-[#26222E] opacity-50" 
                              : "bg-[#4E46FF] hover:bg-[#3d37cc]"
                          )}
                        >
                          {isLoadingQuery ? 'Processing...' : 'Submit'}
                        </Button>
                        <Button 
                          onClick={clearQuery} 
                          variant="outline" 
                          disabled={isLoadingQuery}
                          className="border-gray-700 text-[#CECFD2]"
                        >
                          Clear
                        </Button>
                      </div>
                      
                      {queryResponse && (
                        <div className="mt-6 space-y-4">
                          <div className="bg-[#26222E] p-4 rounded-lg border border-gray-500">
                            <h3 className="font-semibold mb-2 text-[#CECFD2]">Answer:</h3>
                            <p className="whitespace-pre-wrap text-[#CECFD2]">{queryResponse.answer}</p>
                          </div>
                          
                          <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="sources" className="border-gray-700">
                              <AccordionTrigger className="text-[#CECFD2]">Sources</AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-4 p-2">
                                  {queryResponse.sources.map((source, index) => (
                                    <div 
                                      key={index} 
                                      className="bg-[#26222E] p-3 rounded-lg text-sm text-[#CECFD2] border border-gray-500"
                                    >
                                      {source}
                                    </div>
                                  ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      )}
                      
                      {isLoadingQuery && (
                        <div className="flex justify-center items-center p-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4E46FF] border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-center items-center p-8 text-[#CECFD2]">
                      Please select a document from the list to start querying
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};